import route from "mock/route";
import { Children } from "react";

/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  {
    path: "/user",
    layout: false,
    routes: [
      {
        name: "login",
        path: "/user/login",
        component: "./user/login",
      },
    ],
  },
  {
    path: "/base-info",
    name: "基本信息",
    code: "base_info",
    icon: "database", 
    routes: [
      {
        path: "/base-info/area",
        name: "服务片区",
        code: "area",
        icon: "environment",
        component: "./base-info/area",
      },
      {
        path: "/base-info/area/add",
        name: "新建片区",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/area/form",
      },
      {
        path: "/base-info/area/edit",
        name: "编辑片区",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/area/form",
      },
      {
        path: "/base-info/customer",
        name: "商户信息",
        code: "merchant",
        icon: "user",
        component: "./base-info/customer",
      },
      {
        path: "/base-info/customer/add",
        name: "新建商户",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/form",
      },
      {
        path: "/base-info/customer/edit",
        name: "编辑商户",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/form",
      },
      {
        path: "/base-info/customer/view",
        name: "查看商户",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/form",
      },
      {
        path: "/base-info/contract/add",
        name: "新增签订合同",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/contract/form",
      },
      {
        path: "/base-info/contract/edit",
        name: "编辑合同",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/contract/form",
      },
      {
        path: "/base-info/contract/view",
        name: "查看合同",
        icon: "environment",
        hideInMenu: true,
        component: "./base-info/customer/contract/view",
      },
      {
        path: "/base-info/operation",
        name: "运营信息",
        code: "operational_data",
        icon: "setting",
        component: "./base-info/operation",
      },
      {
        path: "/base-info/operation/people/add",
        name: "新建人员",
        icon: "setting",
        hideInMenu: true,
        component: "./base-info/operation/people/form",
      },
      {
        path: "/base-info/operation/people/edit",
        name: "编辑人员",
        icon: "setting",
        hideInMenu: true,
        component: "./base-info/operation/people/form",
      },
    ],
  },
  {
    path: "/garbage-record",
    name: "清运记录",
    code: "wr_record",
    icon: "fileText",
    routes: [
      {
        path: "/garbage-record",
        name: "清运记录",
        icon: "fileText",
        hideInMenu: true,
        component: "./garbage-record",
      },
      {
        path: "/garbage-record/add",
        name: "新建清运记录",
        icon: "fileText",
        hideInMenu: true,
        component: "./garbage-record/form",
      },
    ],
  },

  {
    path: "/garbage-record/edit",
    name: "编辑清运记录",
    icon: "fileText",
    hideInMenu: true,
    component: "./garbage-record/form",
  },
  {
    path: "/expiration-reminder",
    name: "到期提醒",
    code: "reminder",
    icon: "bell",
    component: "./expiration-reminder",
  },
  {
    path: "/reconciliation",
    name: "对账",
    code: "reconcile",
    icon: "AccountBookOutlined",
    routes: [
      {
        path: "/reconciliation/list",
        name: "对账列表",
        code: "reconcile_list",
        icon: "unorderedList",
        component: "./reconciliation/list",
      },
      {
        path: "/reconciliation/list/handle",
        name: "去对账",
        icon: "unorderedList",
        hideInMenu: true,
        component: "./reconciliation/list/handle",
      },
      {
        path: "/reconciliation/list/view",
        name: "查看",
        icon: "unorderedList",
        hideInMenu: true,
        component: "./reconciliation/list/handle",
      },
      {
        path: "/reconciliation/manual",
        name: "手动对账",
        code: "reconcile_manual",
        component: "./reconciliation/manual",
      },
      {
        path: "/reconciliation/manual/view",
        name: "查看",
        hideInMenu: true,
        component: "./reconciliation/manual/view",
      },
      {
        path: "/reconciliation/manual/handle",
        name: "新建手动对账",
        hideInMenu: true,
        component: "./reconciliation/manual/view",
      },
    ],
  },
  {
    path: "/invoice",
    name: "开票",
    code: "ticket",
    icon: "file",
    routes: [
      {
        path: "/invoice/list",
        name: "开票列表",
        code: "ticket_list",
        icon: "container",
        component: "./invoice/list",
      },
      {
        path: "/invoice/list/handle",
        name: "申请开票",
        hideInMenu: true,
        component: "./invoice/list/handle",
      },
      {
        path: "/invoice/list/view",
        name: "查看开票",
        hideInMenu: true,
        component: "./invoice/list/handle",
      },
      {
        path: "/invoice/manual",
        name: "手动开票",
        code: "ticket_manual",
        icon: "form",
        component: "./invoice/manual",
      },
      {
        path: "/invoice/manual/handle",
        name: "发起手动开票",
        hideInMenu: true,
        component: "./invoice/manual/handle",
      },
    ],
  },
  {
    path: "/payment-collection",
    name: "回款",
    code: "received",
    icon: "transaction",
    routes: [
      {
        path: "/payment-collection/list",
        name: "回款列表",
        code: "received_list",
        icon: "profile",
        component: "./payment-collection/list",
      },
      {
        path: "/payment-collection/list/handle",
        name: "完成回款",
        hideInMenu: true,
        component: "./payment-collection/list/handle",
      },
      {
        path: "/payment-collection/list/view",
        name: "查看",
        hideInMenu: true,
        component: "./payment-collection/list/handle",
      },
      {
        path: "/payment-collection/manual",
        name: "手动回款",
        code: "received_manual",
        icon: "carry",
        component: "./payment-collection/manual",
      },
      {
        path: "/payment-collection/manual/handle",
        name: "新建手动回款记录",
        hideInMenu: true,
        component: "./payment-collection/manual/handle",
      },
      {
        path: "/payment-collection/manual/view",
        name: "查看",
        hideInMenu: true,
        component: "./payment-collection/manual/handle",
      },
    ],
  },
  {
    path: "/immediate-payment",
    name: "现结回款",
    code: "cash_received",
    icon: "dollarCircle",
    component: "./immediate-payment",
  },
  {
    path: "/charge",
    name: "充值",
    code: "recharge",
    icon: "wallet",
    routes: [
      {
        path: "/charge",
        name: "充值",
        hideInMenu: true,
        component: "./charge",
      },
      {
        path: "/charge/add",
        name: "充值",
        hideInMenu: true,
        component: "./charge/form",
      },
      {
        path: "/charge/view",
        name: "查看",
        hideInMenu: true,
        component: "./charge/form",
      },
    ],
  },

  {
    path: "/balance-reminder",
    name: "卡余额提醒",
    code: "balance_reminder",
    icon: "alert",
    component: "./balance-reminder",
  },
  {
    path: "/absorption",
    name: "消纳",
    code: "waste_disposal",
    icon: "export",
    routes: [
      {
        path: "/absorption",
        name: "消纳",
        hideInMenu: true,
        component: "./absorption",
      },
      {
        path: "/absorption/add",
        name: "新建消纳",
        hideInMenu: true,
        component: "./absorption/form",
      },
      {
        path: "/absorption/edit",
        name: "编辑",
        hideInMenu: true,
        component: "./absorption/form",
      },
      {
        path: "/absorption/view",
        name: "查看",
        hideInMenu: true,
        component: "./absorption/form",
      },
    ],
  },
   {
    path: "/wasteOrder",
    name: "订单管理",
    code: "order",
    icon: "LineChartOutlined",
    routes: [
      {
        path: "/wasteOrder",
        name: "订单管理",
        hideInMenu: true,
        component: "./wasteOrder",
      }, 
      {
        path: "/wasteOrder/edit",
        name: "编辑",
        hideInMenu: true,
        component: "./wasteOrder/form",
      },
      {
        path: "/wasteOrder/view",
        name: "查看",
        hideInMenu: true,
        component: "./wasteOrder/form",
      },
    ],
  },
  {
    path: "/statistical",
    name: "统计分析",
    code: "data_analyse",
    icon: "barChart",
    component: "./statistical",
  },
  {
    path: "/rule-config",
    name: "规则设置",
    code: "rule_config",
    icon: "control",
    component: "./rule-config",
  },
  {
    path: "/system-config",
    name: "系统设置",
    code: "system_config",
    icon: "tool",
    routes: [
      {
        path: "/system-config",
        name: "成员管理",
        hideInMenu: true,
        component: "./system-config",
      },
      {
        path: "/system-config/user/add",
        name: "添加成员",
        hideInMenu: true,
        component: "./system-config/user/form",
      },
      {
        path: "/system-config/user/edit",
        name: "编辑成员",
        hideInMenu: true,
        component: "./system-config/user/form",
      },
      {
        path: "/system-config/user/view",
        name: "查看成员",
        hideInMenu: true,
        component: "./system-config/user/form",
      },
      {
        path: "/system-config/role",
        name: "角色与权限",
        hideInMenu: true,
        component: "./system-config",
      },
      {
        path: "/system-config/role/add",
        name: "添加角色",
        hideInMenu: true,
        component: "./system-config/role/form",
      },
      {
        path: "/system-config/role/edit",
        name: "编辑角色",
        hideInMenu: true,
        component: "./system-config/role/form",
      },
      {
        path: "/system-config/role/view",
        name: "查看角色",
        hideInMenu: true,
        component: "./system-config/role/form",
      },
    ],
  },
  {
    path: "/",
    redirect: "/base-info/area",
  },
  {
    path: "*",
    layout: false,
    component: "./404",
  },
];
